import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Popover, Select } from 'antd';

import { CHEVRON_DOWN, CLOSE, PLUS } from '../icons';
import { MAX_SORT_LEVELS } from '../../utils/sortLevels';
import { directionAfterSwap, directionWords, reverseDirection, sortWordsFor } from '../../utils/sortWords';

const isDateType = dataType => dataType === 'date' || dataType === 'date-time';
const arrowOf = direction => (direction === 'asc' ? '↑' : '↓');

// The trigger names the system dates the way a person says them.
const SHORT_LABELS = { createdAt: 'Created', updatedAt: 'Updated' };
const triggerLabel = option => SHORT_LABELS[option.value] || option.label;

/**
 * The SORT panel on the items page (KPR-1637, KPE-051 rulings i04 and i05):
 * what the list is ordered by, as one row per level.
 *
 *   Sort: Updated ↓            <- the trigger, in toolbar row 2
 *   Sort by    [Updated date ▾]  [↓ Newest → oldest]
 *   Then by    [Status ▾]        [↑ A → Z]              [×]
 *   [+ Add sort]
 *
 * A level is a searchable field picker (Dates, then the card's elements: each
 * field once, a file element never) and a direction toggle read in the field
 * type's words (utils/sortWords). Every change is written at once, with no
 * Apply: level 1 by `onChange('<field>:<direction>')` and the further levels
 * ("then by", at most two) by `onChangeThen(levels)`, both from
 * hooks/useListSort, so the address (`?sort=` … `?dir3=`) carries the order. A
 * field one level already uses is not offered to another.
 *
 * - `options`: `useListSort`'s `sortOptions` (`{ value, label, dataType }`).
 * - `hasCard`: whether one card is in view; without it only the dates are
 *   offered, and the panel says so.
 * - `source`: where the order in force comes from when the reader has not
 *   chosen one: 'layout' (the card's table layout) or 'default' (the primary
 *   date). The trigger and the panel say so. null once an order is chosen.
 * - `isMobile`: a phone gets a bottom drawer with 44px controls.
 *
 * Keyboard: Enter opens it and focus moves into the panel (Tab walks each
 * level's picker, toggle and remove, then Add sort); Escape closes it and
 * returns focus to the trigger, except while a picker's own list is open,
 * where it closes that list first.
 */
export default function SortLevels({
	field,
	direction,
	sortThen = [],
	options,
	onChange,
	onChangeThen,
	hasCard = false,
	source = null,
	isMobile = false,
}) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef(null);
	const panelRef = useRef(null);

	const optionOf = useCallback(value => options.find(option => option.value === value), [options]);
	const levels = useMemo(() => [{ field, direction }, ...sortThen], [field, direction, sortThen]);
	const used = useMemo(() => new Set(levels.map(level => level.field)), [levels]);

	const typeOf = useCallback(value => optionOf(value)?.dataType || 'text-small', [optionOf]);
	const naturalDirection = useCallback(value => sortWordsFor(typeOf(value)).natural, [typeOf]);

	// ---- Writes: the whole next order in one call ----
	const writeLevel = useCallback((index, next) => {
		if (index === 0) {
			onChange(`${next.field}:${next.direction}`);
			return;
		}
		onChangeThen(sortThen.map((level, i) => (i === index - 1 ? next : level)));
	}, [onChange, onChangeThen, sortThen]);

	const pickField = useCallback((index, nextField) => {
		const current = levels[index];
		writeLevel(index, {
			field: nextField,
			direction: directionAfterSwap(typeOf(current.field), typeOf(nextField), current.direction),
		});
	}, [levels, typeOf, writeLevel]);

	const reverse = useCallback((index) => {
		const current = levels[index];
		writeLevel(index, { field: current.field, direction: reverseDirection(current.direction) });
	}, [levels, writeLevel]);

	const remove = useCallback((index) => {
		onChangeThen(sortThen.filter((_, i) => i !== index - 1));
	}, [onChangeThen, sortThen]);

	// The next level: the first element not yet used (a "then by" an element is
	// the useful one), else the next date.
	const nextField = useMemo(() => {
		const free = options.filter(option => !used.has(option.value));
		return free.find(option => !isDateType(option.dataType)) || free[0] || null;
	}, [options, used]);
	const add = useCallback(() => {
		if (!nextField) return;
		onChangeThen([...sortThen, { field: nextField.value, direction: naturalDirection(nextField.value) }]);
	}, [nextField, onChangeThen, sortThen, naturalDirection]);

	const addReason = levels.length >= MAX_SORT_LEVELS
		? 'A list sorts by up to three fields.'
		: !nextField
			? 'Every field is already in use.'
			: null;

	// ---- The field picker's options: Dates, then the card's elements ----
	const groupsFor = useCallback((index) => {
		const mine = levels[index].field;
		const offered = options.filter(option => option.value === mine || !used.has(option.value));
		// A level the card no longer offers is about to be dropped; show it as it is.
		if (!offered.some(option => option.value === mine)) offered.unshift({ value: mine, label: mine, dataType: 'text-small' });
		const entry = option => ({ value: option.value, label: option.label });
		const dates = offered.filter(option => isDateType(option.dataType)).map(entry);
		const elements = offered.filter(option => !isDateType(option.dataType)).map(entry);
		return [
			...(dates.length ? [{ label: 'Dates', options: dates }] : []),
			...(elements.length ? [{ label: 'Elements', options: elements }] : []),
		];
	}, [levels, options, used]);

	// ---- Focus ----
	// Into the panel when it opens (a popover's content is not next to the
	// trigger in the page, so Tab would never reach it); back to the trigger
	// when it closes by Escape.
	const focusPanel = useCallback(() => {
		const panel = panelRef.current;
		if (!panel || panel.contains(document.activeElement)) return;
		const control = panel.querySelector('.sort-levels__field input') || panel.querySelector('button:not([disabled])');
		if (control && control.focus) control.focus({ preventScroll: true });
	}, []);
	const panelNode = useCallback((node) => {
		panelRef.current = node;
		if (node) focusPanel();
	}, [focusPanel]);

	const close = useCallback(({ restoreFocus = false } = {}) => {
		setOpen(false);
		if (restoreFocus && triggerRef.current && triggerRef.current.focus) triggerRef.current.focus();
	}, []);

	const onPanelKeyDown = useCallback((event) => {
		if (event.key !== 'Escape') return;
		// A picker with its list open closes that first, and only that.
		if (event.target.closest && event.target.closest('.ant-select-open')) {
			event.stopPropagation();
			return;
		}
		event.stopPropagation();
		close({ restoreFocus: true });
	}, [close]);

	// ---- The trigger: "Sort: Updated ↓" ----
	const lead = optionOf(field) || { value: field, label: field, dataType: 'text-small' };
	const more = sortThen.length;
	const sourceText = source === 'layout' ? 'table default' : source === 'default' ? 'default' : null;
	const triggerName = [
		`Sort: ${triggerLabel(lead)}, ${directionWords(lead.dataType, direction)}`,
		more ? `then ${more} more ${more === 1 ? 'level' : 'levels'}` : null,
		sourceText ? `${sourceText} order` : null,
	].filter(Boolean).join(', ');

	const trigger = (
		<Button
			ref={triggerRef}
			className="items-page__sort-field sort-levels__trigger"
			aria-haspopup="dialog"
			aria-expanded={open}
			aria-label={triggerName}
			onClick={isMobile ? () => setOpen(true) : undefined}
			data-sort-levels-trigger
		>
			<span className="sort-levels__lead">Sort:</span>
			<span className="sort-levels__trigger-field">{triggerLabel(lead)}</span>
			<span className="sort-levels__arrow" aria-hidden="true">{arrowOf(direction)}</span>
			{more ? <span className="sort-levels__more" aria-hidden="true">+{more}</span> : null}
			{sourceText ? <span className="sort-levels__source" aria-hidden="true">{sourceText}</span> : null}
			<CHEVRON_DOWN aria-hidden="true" className="sort-levels__caret" />
		</Button>
	);

	const panel = (
		<div
			ref={panelNode}
			className={`sort-levels${isMobile ? ' sort-levels--drawer' : ''}`}
			role="group"
			aria-label="Sort"
			onKeyDown={onPanelKeyDown}
			data-testid="sort-levels"
		>
			{sourceText ? (
				<p className="sort-levels__note">
					{source === 'layout'
						? 'No order chosen: this card’s table layout sets one. Change a level to choose your own.'
						: 'No order chosen: items run by their primary date, newest first. Change a level to choose your own.'}
				</p>
			) : null}
			<ol className="sort-levels__list">
				{levels.map((level, index) => {
					const lane = index === 0 ? 'Sort by' : 'Then by';
					const laneName = index === 0 ? 'Sort by' : `Then by (level ${index + 1})`;
					const type = typeOf(level.field);
					const words = directionWords(type, level.direction);
					const label = optionOf(level.field)?.label || level.field;
					return (
						<li key={index} className="sort-levels__row" data-sort-level={index + 1}>
							<span className="sort-levels__lane">{lane}</span>
							<Select
								className="sort-levels__field"
								showSearch
								value={level.field}
								options={groupsFor(index)}
								optionFilterProp="label"
								onChange={value => pickField(index, value)}
								popupMatchSelectWidth={false}
								classNames={{ popup: { root: 'sort-levels__popup' } }}
								notFoundContent="No field matches"
								placeholder="Choose a field"
								aria-label={laneName}
							/>
							<Button
								className="sort-levels__direction"
								onClick={() => reverse(index)}
								aria-label={`Direction of ${label}: ${words}. Press to reverse.`}
								data-direction={level.direction}
							>
								<span className="sort-levels__arrow" aria-hidden="true">{arrowOf(level.direction)}</span>
								<span className="sort-levels__words">{words}</span>
							</Button>
							{index > 0 ? (
								<Button
									className="sort-levels__remove"
									type="text"
									icon={<CLOSE aria-hidden="true" />}
									aria-label={`Remove then by ${label}`}
									onClick={() => remove(index)}
								/>
							) : <span className="sort-levels__remove-gap" aria-hidden="true" />}
						</li>
					);
				})}
			</ol>
			<div className="sort-levels__foot">
				<Button className="sort-levels__add" icon={<PLUS aria-hidden="true" />} disabled={Boolean(addReason)} onClick={add}>
					Add sort
				</Button>
				{addReason ? <span className="sort-levels__reason" data-sort-add-reason>{addReason}</span> : null}
			</div>
			{hasCard ? null : (
				<p className="sort-levels__reason">Choose one card to sort by its elements.</p>
			)}
		</div>
	);

	if (isMobile) {
		return (
			<>
				{trigger}
				<Drawer
					open={open}
					placement="bottom"
					height="auto"
					title="Sort"
					className="sort-levels__drawer"
					keyboard={false}
					onClose={() => close({ restoreFocus: true })}
					afterOpenChange={visible => { if (visible) focusPanel(); }}
					destroyOnHidden
				>
					{panel}
				</Drawer>
			</>
		);
	}

	return (
		<Popover
			open={open}
			onOpenChange={next => (next ? setOpen(true) : close())}
			afterOpenChange={visible => { if (visible) focusPanel(); }}
			trigger="click"
			placement="bottomLeft"
			autoAdjustOverflow={{ adjustX: true, adjustY: true, shiftX: true }}
			destroyOnHidden
			content={panel}
			overlayClassName="sort-levels__popover"
		>
			{trigger}
		</Popover>
	);
}
